import { Injectable, inject, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject, Subscription } from 'rxjs';
import { distinctUntilChanged, skip, filter } from 'rxjs/operators';

import { ConfirmationDialogService } from './confirmation-dialog.service';
import { TranslateService } from '@ngx-translate/core';
import { FsBaseService } from './fs-base.service';
import { AppGlobalService } from './app.global.service';
import { MergeStatus, ACTIVE_MERGE_STATUSES, MERGE_LISTENER_STATUSES } from '../pages/settings/branch-management/repo-management/repo-management.types';

export interface MergeStatusInfo {
  isMergeInProgress: boolean;
  mergeId?: string;
  initiatedBy?: string;
  initiatedById?: string; // User ID/email for comparison
  sourceBranchId?: string;
  sourceBranchName?: string;
  targetBranchId?: string;
  targetBranchName?: string;
  status?: string;
  showPopup?: boolean; // Flag to indicate if popup should be shown
  cancelledBy?: string; // Display name of user who cancelled
  cancelledById?: string; // User ID/email of who cancelled for comparison
}

@Injectable({
  providedIn: 'root'
})
export class MergeStatusService implements OnDestroy {
  private readonly router = inject(Router);
  private readonly fsService = inject(FsBaseService);
  private readonly appGlobalService = inject(AppGlobalService);
  private readonly confirmationDialogService = inject(ConfirmationDialogService);
  private readonly translateService = inject(TranslateService);

  private readonly mergeStatusSubject = new BehaviorSubject<MergeStatusInfo>({ isMergeInProgress: false });
  public readonly mergeStatus$ = this.mergeStatusSubject.asObservable();

  private mergeTrackerSubscriptions: Subscription[] = [];
  private statusChangeSubscription: Subscription | null = null;
  private readonly routeChangeSubscription: Subscription | null = null;
  private currentAppId: string | null = null;

  // Flag to prevent multiple popups
  private isPopupShowing = false;

  // Flag to track if user already dismissed the in-progress popup (don't re-show on status transitions)
  private mergeInProgressDismissed = false;

  // Flag to track if user already dismissed a terminal popup (cancelled/completed/failed) - prevents re-trigger
  private terminalPopupDismissed = false;

  // Counter to track initial load responses (don't show popup until all initial responses received)
  private initialLoadCounter = 0;
  private expectedSubscriptionCount = 2;

  // Flag to track if merge was cancelled by user (skip showing popup)
  private cancelledByUser = false;

  // Flag to track if user is initiating a merge (skip popup for their own merge)
  private isUserInitiatingMerge = false;

  // Track merge IDs already handled by the initiating user (prevents popup after dialog closes)
  private readonly handledMergeIds = new Set<string>();
  private lastActiveMergeId: string | null = null;

  // Delay before retrying user-name lookup when appmetaInfo is still loading
  private readonly mergeUserNameRetryDelayMs = 1000;

  constructor() {
    // Listen for route changes to unsubscribe when navigating away from app
    this.routeChangeSubscription = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe(() => {
      if (!this.isInsideApp()) {
        // Navigated away from app - unsubscribe from merge status
        this.unsubscribeFromMergeStatus();
        this.clearMergeStatus();
      }
    });
  }

  /**
   * Check if user is inside an app by checking appId in appGlobalService
   * Returns false if appId is empty, null, undefined, or the placeholder 'appId'
   */
  private isInsideApp(): boolean {
    const appId = this.appGlobalService.get('appId');
    return !!appId && appId !== 'appId';
  }

  /**
   * Mark merge as cancelled by user to prevent showing popup on status change
   */
  markAsCancelledByUser(): void {
    this.cancelledByUser = true;
  }

  /**
   * Mark that user is initiating a merge (inside merge dialog)
   * This prevents showing the popup for their own merge
   */
  markAsUserInitiatingMerge(): void {
    this.isUserInitiatingMerge = true;
  }

  /**
   * Clear the user initiating merge flag
   */
  clearUserInitiatingMerge(): void {
    this.isUserInitiatingMerge = false;
  }

  /**
   * Mark a merge as already handled by the initiating user.
   */
  markMergeAsHandled(mergeId: string): void {
    if (mergeId) {
      this.handledMergeIds.add(mergeId);
    }
  }

  /**
   * Called by merge-branch component when Firestore detects merge completed.
   */
  triggerMergeCompletedPopup(targetInfo?: { targetBranchUrl: string; targetBranchName: string }): void {
    this.isUserInitiatingMerge = false;
    this.appGlobalService.write('isMergeInProgress', false);
    this.appGlobalService.write('mergeDetails', null);
    this.showMergeCompletedPopup(targetInfo);
  }

  /**
   * Called by merge-branch component when Firestore detects merge failed.
   */
  triggerMergeFailedPopup(): void {
    this.isUserInitiatingMerge = false;
    this.appGlobalService.write('isMergeInProgress', false);
    this.appGlobalService.write('mergeDetails', null);
    this.showMergeFailedPopup({} as MergeStatusInfo);
  }

  /**
   * Trigger view-only mode manually (called after /merge API success and dialog close)
   */
  activateViewOnlyMode(mergeInfo: MergeStatusInfo): void {
    this.updateMergeStatus({ ...mergeInfo, isMergeInProgress: true });
    this.showMergeInProgressPopup(mergeInfo);
  }

  /**
   * Subscribe to MergeHistoryTracker for the current app   */
  subscribeToMergeStatus(appId: string): void {
    // Don't subscribe if not inside an app
    if (!this.isInsideApp()) {
      this.unsubscribeFromMergeStatus();
      return;
    }

    if (this.currentAppId === appId && this.mergeTrackerSubscriptions.length > 0) {
      return;
    }

    this.unsubscribeFromMergeStatus();
    // Clear stale merge status from previous branch immediately
    this.clearMergeStatus(false);
    this.currentAppId = appId;

    // Clear handled merge IDs from previous context
    this.handledMergeIds.clear();
    this.lastActiveMergeId = null;

    // Reset initial load flag - use a counter since we have multiple subscriptions
    this.initialLoadCounter = 0;
    this.expectedSubscriptionCount = 2; // Source and Target subscriptions

    const appVersion = this.appGlobalService.get('appVersion');
    if (!appVersion) {
      console.warn('Unable to get appVersion for MergeHistoryTracker subscription');
      return;
    }

    const collectionName = `MergeHistoryTracker_${appVersion}`;

    // Subscribe to merges where this app is SOURCE
    this.subscribeToMergeQuery(collectionName, 'sourceBranchId', appId);

    // Subscribe to merges where this app is TARGET
    this.subscribeToMergeQuery(collectionName, 'targetBranchId', appId);
    // For member apps, also subscribe to the suite parent's merge tracker.
    // suiteId (not parentAppSid) is the suite BRANCH SID stored in sourceBranchId/targetBranchId.
    // parentAppSid is the suite's root app SID — it does NOT appear in MergeHistoryTracker records.
    const suiteId = this.appGlobalService.get('suiteId');
    if (this.appGlobalService.isMemberApp() && suiteId) {
      this.expectedSubscriptionCount += 2;
      this.subscribeToMergeQuery(collectionName, 'sourceBranchId', suiteId);
      this.subscribeToMergeQuery(collectionName, 'targetBranchId', suiteId);
    }

    // Listen for status changes
    this.setupStatusChangeListener();
  }

  private subscribeToMergeQuery(collectionName: string, branchField: string, appId: string): void {
    const queryObject = {
      collectionName: collectionName,
      filter: [
        { field: branchField, operator: '==', value: appId },
        { field: 'status', operator: 'in', value: MERGE_LISTENER_STATUSES },
        { field: 'recDeleted', operator: '==', value: false }
      ]
    };

    const subscription = this.fsService.fireService(queryObject).subscribe({
      next: (res: any) => this.handleMergeTrackerResponse(res),
      error: (error: any) => console.error(`Error subscribing to MergeHistoryTracker:`, error)
    });

    this.mergeTrackerSubscriptions.push(subscription);
  }

  private setupStatusChangeListener(): void {
    this.statusChangeSubscription = this.mergeStatus$.pipe(
      distinctUntilChanged((prev, curr) =>
        prev.isMergeInProgress === curr.isMergeInProgress &&
        prev.status === curr.status
      ),
      skip(1) // Skip initial value
    ).subscribe(status => {
      if (this.cancelledByUser) {
        if (!status.isMergeInProgress) {
          this.cancelledByUser = false;
          this.confirmationDialogService.close();
          this.isPopupShowing = false;
        }
        return;
      }

      if (this.isUserInitiatingMerge) {
        return;
      }
      if (!status.isMergeInProgress) {
        this.confirmationDialogService.close();
        this.isPopupShowing = false;
        this.mergeInProgressDismissed = false;
      } else {
        // New merge started - reset terminal popup dismissed flag
        this.terminalPopupDismissed = false;
      }

      if (!status.showPopup) {
        return;
      }
      if (status.isMergeInProgress) {
        this.showMergeInProgressPopup(status);
      } else {
        setTimeout(() => {
          if (status.status === MergeStatus.MERGE_CANCELLED) {
            this.showMergeCancelledPopup(status);
          } else if (status.status === MergeStatus.MERGE_FAILED) {
            this.showMergeFailedPopup(status);
          } else {
            this.showMergeCompletedPopup();
          }
        });
      }
    });
  }

  /**
   * Show non-closable popup when merge is in progress
   */
  private showMergeInProgressPopup(status: MergeStatusInfo, hasRetried = false): void {
    // Don't show popup on lifecycle pages
    if (!this.isInsideApp()) {
      return;
    }

    // Don't re-show if user already dismissed the in-progress popup
    if (this.mergeInProgressDismissed) {
      return;
    }

    const appMetaInfo = this.appGlobalService.get('appmetaInfo') || {};
    const isNameMissing = !!status.initiatedById && !appMetaInfo[status.initiatedById];

    // On refresh, merge status can arrive before appmetaInfo. Delay once to avoid showing raw IDs.
    if (!hasRetried && isNameMissing) {
      setTimeout(() => {
        this.showMergeInProgressPopup(status, true);
      }, this.mergeUserNameRetryDelayMs);
      return;
    }

    this.isPopupShowing = true;

    // Re-resolve user name from appmetaInfo (populated by AppUserMetaInfo listener)
    const message = (status.initiatedById && appMetaInfo[status.initiatedById]) || status.initiatedBy;
    const translationParams = { user: message };
    const translationMessage = this.translateService.instant('MERGE_STARTED_OTHER_USER', translationParams);
    const header = this.translateService.instant('MERGE_IN_PROGRESS');

    this.confirmationDialogService.confirm({
      key: 'mergeStatusPopup',
      message: translationMessage,
      header: header,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translateService.instant('OK'),
      rejectVisible: false,
      closeOnEscape: false,
      accept: () => {
        this.isPopupShowing = false;
        this.mergeInProgressDismissed = true;
        this.reloadCurrentRoute();
      }
    });
  }

  /**
   * Show non-closable popup when merge is completed
   */
  private showMergeCompletedPopup(targetInfo?: { targetBranchUrl: string; targetBranchName: string }): void {
    // Don't show popup on lifecycle pages
    if (!this.isInsideApp()) {
      return;
    }

    if (this.isPopupShowing || this.terminalPopupDismissed) {
      return;
    }

    this.isPopupShowing = true;

    let message = this.translateService.instant('MERGE_COMPLETED_MESSAGE');
    if (targetInfo?.targetBranchUrl) {
      const goToLabel = this.translateService.instant('GO_TO_TARGET_APPLICATION', { targetBranch: targetInfo.targetBranchName });
      message += `<br/><br/><a href="${targetInfo.targetBranchUrl}" target="_blank" class="merge-target-link">${goToLabel} <i class="pi pi-external-link"></i></a>`;
    }

    this.confirmationDialogService.confirm({
      key: 'mergeStatusPopup',
      message: message,
      header: this.translateService.instant('MERGE_COMPLETED'),
      icon: 'pi pi-check-circle',
      acceptLabel: this.translateService.instant('OK'),
      rejectVisible: false,
      closeOnEscape: false,
      accept: () => {
        this.isPopupShowing = false;
        this.terminalPopupDismissed = true;
        // Clear any residual merge state
        this.clearMergeStatus(false);
        this.reloadCurrentRoute();
      }
    });
  }

  /**
   * Show non-closable popup when merge has failed
   */
  private showMergeFailedPopup(status: MergeStatusInfo): void {
    // Don't show popup on lifecycle pages
    if (!this.isInsideApp()) {
      return;
    }

    if (this.isPopupShowing || this.terminalPopupDismissed) {
      return;
    }

    this.isPopupShowing = true;

    this.confirmationDialogService.confirm({
      key: 'mergeStatusPopup',
      message: this.translateService.instant('MERGE_FAILED_POPUP_MESSAGE'),
      header: this.translateService.instant('MERGE_FAILED'),
      icon: 'pi pi-times-circle',
      acceptLabel: this.translateService.instant('OK'),
      rejectVisible: false,
      closeOnEscape: false,
      accept: () => {
        this.isPopupShowing = false;
        this.terminalPopupDismissed = true;
        this.clearMergeStatus(false, status?.status);
        this.reloadCurrentRoute();
      }
    });
  }

  /**
   * Show non-closable popup when merge is cancelled
   * Shows username only if cancelled by another user
   */
  private showMergeCancelledPopup(status: MergeStatusInfo): void {
    // Don't show popup on lifecycle pages
    if (!this.isInsideApp()) {
      return;
    }

    if (this.isPopupShowing || this.terminalPopupDismissed) {
      return;
    }

    this.isPopupShowing = true;

    // Check if current user cancelled the merge
    const currentUserEmail = this.appGlobalService.get('userEmail');
    const currentUserId = this.appGlobalService.get('user')?.sid;
    const isCancelledByCurrentUser = status.cancelledById === currentUserEmail || status.cancelledById === currentUserId;

    // Re-resolve cancelled-by user name from appmetaInfo (populated by AppUserMetaInfo listener)
    const appMetaInfo = this.appGlobalService.get('appmetaInfo') || {};
    const cancelledByName = (status.cancelledById && appMetaInfo[status.cancelledById]) || status.cancelledBy;

    // Show different message based on who cancelled
    const message = isCancelledByCurrentUser
      ? this.translateService.instant('MERGE_CANCELLED_SUCCESS')
      : this.translateService.instant('MERGE_CANCELLED_BY_USER', { userName: cancelledByName });

    this.confirmationDialogService.confirm({
      key: 'mergeStatusPopup',
      message: message,
      header: this.translateService.instant('MERGE_CANCELLED'),
      icon: 'pi pi-times-circle',
      acceptLabel: this.translateService.instant('OK'),
      rejectVisible: false,
      closeOnEscape: false,
      accept: () => {
        this.isPopupShowing = false;
        this.terminalPopupDismissed = true;
        this.reloadCurrentRoute();
      }
    });
  }

  /**
   * Reload the current route
   */
  private reloadCurrentRoute(): void {
    const currentUrl = this.router.url;
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigateByUrl(currentUrl);
    });
  }

  private handleMergeTrackerResponse(res: any): void {
    // Track initial load counter for subscription management
    const isInitialLoad = this.initialLoadCounter < this.expectedSubscriptionCount;
    this.initialLoadCounter++;

    if (res && res.length > 0) {
      const activeEvents = res.filter((dt: any) => {
        const type = dt.type || dt.payload?.type;
        return type === 'added' || type === 'modified';
      });

      if (activeEvents.length > 0) {
        const activeMergeEvent = activeEvents.find((dt: any) => {
          const d = dt.payload?.doc?.data() || dt;
          return ACTIVE_MERGE_STATUSES.includes(d.status) ||
            d.status === MergeStatus.MERGE_CHANGES_REVIEW_IN_PROGRESS;
        });
        const docData = (activeMergeEvent || activeEvents[0]).payload?.doc?.data() || (activeMergeEvent || activeEvents[0]);
        const createdById = docData.createdBy;
        // Get display name from appMetaInfo
        const appMetaInfo = this.appGlobalService.get('appmetaInfo') || {};
        const initiatedByName = appMetaInfo[createdById] || createdById;
        const isActiveMerge = ACTIVE_MERGE_STATUSES.includes(docData.status);
        const shouldBlock = isActiveMerge || docData.status === MergeStatus.MERGE_CHANGES_REVIEW_IN_PROGRESS;

        if (shouldBlock) {
          // Get branch names - try direct field first, then lookup from appMetaInfo
          const sourceBranchName = docData.sourceBranchName || appMetaInfo[docData.sourceBranchId] || docData.sourceBranchId;
          const targetBranchName = docData.targetBranchName || appMetaInfo[docData.targetBranchId] || docData.targetBranchId;

          const mergeInfo: MergeStatusInfo = {
            isMergeInProgress: true,
            mergeId: docData.sid,
            initiatedBy: initiatedByName,
            initiatedById: createdById, // Store original ID for comparison
            sourceBranchId: docData.sourceBranchId,
            sourceBranchName: sourceBranchName,
            targetBranchId: docData.targetBranchId,
            targetBranchName: targetBranchName,
            status: docData.status,
            showPopup: true
          };

          this.updateMergeStatus(mergeInfo);
          this.lastActiveMergeId = docData.sid;
        } else {

          if (this.lastActiveMergeId && this.lastActiveMergeId !== docData.sid) {
            return;
          }

          if (this.mergeStatusSubject.value.isMergeInProgress &&
              this.mergeStatusSubject.value.mergeId !== docData.sid) {
            return;
          }
          if (!this.mergeStatusSubject.value.isMergeInProgress &&
              this.mergeStatusSubject.value.status === docData.status) {
            return;
          }
          if (this.handledMergeIds.has(docData.sid)) {
            this.clearMergeStatus(false, docData.status);
            return;
          }

          if (docData.status === MergeStatus.MERGE_CANCELLED) {
            const cancelledById = docData.modifiedBy || docData.createdBy;
            const cancelledByName = appMetaInfo[cancelledById] || cancelledById;
            this.clearMergeStatusWithInfo(!isInitialLoad, docData.status, cancelledByName, cancelledById);
          } else if (docData.status === MergeStatus.MERGE_FAILED) {
            this.clearMergeStatus(!isInitialLoad, docData.status);
          } else {
            this.clearMergeStatus(!isInitialLoad, docData.status);
          }
        }
        return;
      }

      const removedEvents = res.filter((dt: any) => {
        const type = dt.type || dt.payload?.type;
        return type === 'removed';
      });

      if (removedEvents.length > 0) {
        this.clearMergeStatus(!isInitialLoad);
      }
    } else if (!isInitialLoad && this.mergeStatusSubject.value.isMergeInProgress) {
      this.clearMergeStatus(false);
    }
  }

  private updateMergeStatus(status: MergeStatusInfo): void {
    this.appGlobalService.write('isMergeInProgress', status.isMergeInProgress);
    this.appGlobalService.write('mergeDetails', status);
    this.mergeStatusSubject.next(status);
  }

  private clearMergeStatus(showPopup: boolean = false, status?: string): void {
    this.appGlobalService.write('isMergeInProgress', false);
    this.appGlobalService.write('mergeDetails', null);
    this.isUserInitiatingMerge = false;
    this.cancelledByUser = false;
    this.mergeInProgressDismissed = false;
    this.mergeStatusSubject.next({ isMergeInProgress: false, showPopup: showPopup, status: status });
  }

  private clearMergeStatusWithInfo(showPopup: boolean, status: string, cancelledBy: string, cancelledById: string): void {
    this.appGlobalService.write('isMergeInProgress', false);
    this.appGlobalService.write('mergeDetails', null);
    this.isUserInitiatingMerge = false;
    this.cancelledByUser = false;
    this.mergeInProgressDismissed = false;
    this.mergeStatusSubject.next({
      isMergeInProgress: false,
      showPopup: showPopup,
      status: status,
      cancelledBy: cancelledBy,
      cancelledById: cancelledById
    });
  }

  unsubscribeFromMergeStatus(): void {
    this.mergeTrackerSubscriptions.forEach(sub => sub.unsubscribe());
    this.mergeTrackerSubscriptions = [];
    this.statusChangeSubscription?.unsubscribe();
    this.statusChangeSubscription = null;
    this.currentAppId = null;
    this.initialLoadCounter = 0;
    this.cancelledByUser = false;
    this.mergeInProgressDismissed = false;
  }

  get isMergeInProgress(): boolean {
    return this.mergeStatusSubject.value.isMergeInProgress;
  }

  /**
   * Get current merge status info
   */
  getMergeStatus(): MergeStatusInfo | null {
    const status = this.mergeStatusSubject.value;
    return status.isMergeInProgress ? status : null;
  }

  clearMergeState(status?: string): void {
    this.clearMergeStatus(false, status);
  }

  ngOnDestroy(): void {
    this.unsubscribeFromMergeStatus();
    this.routeChangeSubscription?.unsubscribe();
  }
}


